/**
 * Inventory Manager — frontend feature registration.
 *
 * A top-level feature, not a catalog sub-area. `shared/src/features.ts` has
 * always declared `inventory` as a sibling of `products` and `warehouses`
 * ("composes products + warehouses"), and the backend has always had
 * `backend/src/features/inventory/`; only the frontend disagreed by nesting this
 * under `features/catalog/`. That nesting also produced a real bug: the nav item
 * sat at `/catalog/inventory`, and because nav active state is prefix-based, both
 * "Browse catalog" and "Inventory" highlighted at once.
 *
 * Gating note: Inventory is now behind the `inventory` flag rather than
 * `products`. Both are `enabled: true`, so there is no behaviour change today —
 * but disabling `products` no longer hides Inventory.
 */
import React, { lazy } from 'react'
import { Navigate, Route } from 'react-router-dom'
import { Boxes } from 'lucide-react'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { lazyRoute } from '../routeHelpers'
import type { FrontendFeature } from '../types'

const InventoryPage = lazy(() => import('./pages/InventoryPage'))

const feature: FrontendFeature = {
  navItems: [
    {
      path: '/inventory',
      label: 'Inventory',
      icon: Boxes,
      requiredPermissions: ['products:read'],
    },
  ],
  routes: [
    <Route
      key="inventory"
      path="/inventory"
      element={
        <ProtectedRoute requiredPermissions={['products:read']}>
          {lazyRoute(<InventoryPage />)}
        </ProtectedRoute>
      }
    />,
    // Legacy path. Lives with the target feature so old bookmarks keep working
    // even if the catalog feature is ever disabled.
    <Route
      key="inventory-legacy"
      path="/catalog/inventory"
      element={<Navigate to="/inventory" replace />}
    />,
  ],
}

export default feature
